<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AgentBotModal from './components/AgentBotModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();

const agentBots = useMapGetter('agentBots/getBots');
const uiFlags = useMapGetter('agentBots/getUIFlags');

const selectedBot = ref({});
const loading = ref({});
const modalType = ref(MODAL_TYPES.CREATE);
const agentBotModalRef = ref(null);
const agentBotDeleteDialogRef = ref(null);

const tableHeaders = computed(() => {
  return [
    t('AGENT_BOTS.LIST.TABLE_HEADER.DETAILS'),
    t('AGENT_BOTS.LIST.TABLE_HEADER.URL'),
  ];
});

const selectedBotName = computed(() => selectedBot.value?.name || '');

const openAddModal = () => {
  modalType.value = MODAL_TYPES.CREATE;
  selectedBot.value = {};
  agentBotModalRef.value.dialogRef.open();
};

const openEditModal = bot => {
  modalType.value = MODAL_TYPES.EDIT;
  selectedBot.value = bot;
  agentBotModalRef.value.dialogRef.open();
};

const openDeletePopup = bot => {
  selectedBot.value = bot;
  agentBotDeleteDialogRef.value.open();
};

const deleteAgentBot = async id => {
  try {
    await store.dispatch('agentBots/delete', id);
    useAlert(t('AGENT_BOTS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_BOTS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
    selectedBot.value = {};
  }
};

onMounted(() => {
  store.dispatch('agentBots/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('AGENT_BOTS.LIST.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('AGENT_BOTS.HEADER')"
        :description="t('AGENT_BOTS.DESCRIPTION')"
        :link-text="t('AGENT_BOTS.LEARN_MORE')"
        href="https://www.chatwoot.com/docs/product/others/agent-bots"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="t('AGENT_BOTS.ADD.TITLE')"
            @click="openAddModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <!-- Bot List -->
    <div v-if="agentBots.length" class="flex flex-col gap-2 p-4">
      <div
        v-for="bot in agentBots"
        :key="bot.id"
        class="bg-white dark:bg-n-slate-1 rounded-lg shadow-sm border border-n-weak p-6 hover:shadow-md transition-shadow"
      >
        <div class="flex items-center justify-between">
          <!-- Bot Info -->
          <div class="flex items-center gap-4">
            <Avatar
              :src="bot.thumbnail"
              :name="bot.name"
              :size="48"
              icon-name="i-lucide-bot"
            />
            <div>
              <h3 class="font-semibold text-lg text-n-slate-12">
                {{ bot.name }}
              </h3>
              <p class="text-sm text-n-slate-10">
                {{ bot.description || t('AGENT_BOTS.WEBHOOK.DESCRIPTION') }}
              </p>
              <div class="flex items-center gap-2 mt-1">
                <span
                  class="text-xs text-n-slate-9 bg-n-slate-3 px-2 py-1 rounded"
                >
                  {{ t('AGENT_BOTS.TYPES.WEBHOOK') }}
                </span>
                <span
                  v-if="bot.bot_config?.is_csml"
                  class="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded"
                >
                  {{ t('AGENT_BOTS.GLOBAL_BOT_BADGE') }}
                </span>
              </div>
            </div>
          </div>

          <!-- Bot Actions -->
          <div class="flex items-center gap-2">
            <Button
              icon="i-lucide-edit"
              variant="ghost"
              size="sm"
              @click="openEditModal(bot)"
            />
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              size="sm"
              @click="openDeletePopup(bot)"
            />
          </div>
        </div>

        <!-- Webhook URL -->
        <div class="mt-4 pt-4 border-t border-n-weak">
          <div class="flex items-center justify-between">
            <div>
              <span class="text-sm font-medium text-n-slate-11">{{
                tableHeaders[1]
              }}</span>
              <p class="text-sm text-n-slate-9 font-mono">
                {{
                  bot.outgoing_url ||
                  bot.bot_config?.webhook_url ||
                  'No webhook URL'
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!uiFlags.isFetching"
      class="flex flex-col items-center justify-center p-12 text-center"
    >
      <div
        class="w-16 h-16 rounded-full bg-n-slate-3 flex items-center justify-center mb-4"
      >
        <i class="i-lucide-bot text-2xl text-n-slate-8" />
      </div>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('AGENT_BOTS.LIST.404').split('.')[0] }}
      </h3>
      <p class="text-n-slate-10 mb-4">
        {{ t('AGENT_BOTS.LIST.404').split('. ')[1] }}
      </p>
      <Button
        icon="i-lucide-circle-plus"
        :label="t('AGENT_BOTS.ADD.TITLE')"
        @click="openAddModal"
      />
    </div>

    <!-- Agent Bot Modal -->
    <AgentBotModal
      ref="agentBotModalRef"
      :type="modalType"
      :selected-bot="selectedBot"
    />

    <!-- Delete Confirmation Dialog -->
    <Dialog
      ref="agentBotDeleteDialogRef"
      type="confirmation"
      :title="t('AGENT_BOTS.DELETE.TITLE')"
      :description="
        t('AGENT_BOTS.DELETE.CONFIRM.MESSAGE', { name: selectedBotName })
      "
      :confirm-button-label="t('AGENT_BOTS.DELETE.CONFIRM.YES')"
      :cancel-button-label="t('AGENT_BOTS.DELETE.CONFIRM.NO')"
      @confirm="deleteAgentBot(selectedBot.id)"
    />
  </SettingsLayout>
</template>
